import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  input,
  viewChild,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CdkConnectedOverlay,
  ConnectedPosition,
  CdkOverlayOrigin,
  OverlayModule,
} from '@angular/cdk/overlay';
import { Observable, of } from 'rxjs';
import { startWith, debounceTime, switchMap, catchError } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { heroChevronDown } from '@ng-icons/heroicons/outline';

import { NgIconComponent, provideIcons } from '@ng-icons/core';

@Component({
  selector: 'ui-async-select',
  imports: [CommonModule, ReactiveFormsModule, NgIconComponent, OverlayModule],
  templateUrl: './asyncSelect.component.html',
  viewProviders: [
    provideIcons({
      heroChevronDown,
    }),
  ],
  styles: ['.gridArea {grid-area: 1 / 1 / 2 / 3;}'],
})
export class AsyncSelectComponent implements OnInit {
  readonly overlayDir = viewChild.required(CdkConnectedOverlay);
  readonly groupName = input.required<FormGroup>();
  readonly controlName = input.required<string>();
  readonly isMultiSelect = input(false);
  isInputFocused = false;

  readonly fetchOptions = input.required<
    (query: string) => Observable<
      {
        label: string;
        value: string;
      }[]
    >
  >();
  selectedOptions: { label: string; value: string }[] = [];
  filteredOptions: { label: string; value: string }[] = [];
  originalOptions: { label: string; value: string }[] = [];
  currentInputValue = '';
  panelOpen = false;
  readonly formResetTrigger = input(false);
  readonly origin = viewChild.required(CdkOverlayOrigin);
  readonly inputElement = viewChild.required<ElementRef>('inputRef');
  options: any[] = []; // Your option type here
  readonly inputElementRef = viewChild.required<ElementRef>('searchInput');
  searchControl = new FormControl();

  ngOnInit(): void {
    // Accessing the form control using groupName and controlName

    this.searchControl?.valueChanges
      .pipe(
        startWith(''), // Optional: Start with an empty string to fetch initial options
        debounceTime(300), // Wait 300ms after the last keystroke before sending the query
        switchMap((value) =>
          this.fetchOptions()(value).pipe(
            catchError((error) => {
              console.error(error);
              return of([]); // Return an empty array on error
            })
          )
        )
      )
      .subscribe((options) => {
        this.originalOptions = options;
        // Reset selectedOptions if necessary
        this.refreshOptions();
      });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    // Check if the click is outside the overlay AND not on the input element
    const clickTarget = event.target as HTMLElement;
    const isClickInsideOverlay =
      this.overlayDir()?.overlayRef?.overlayElement.contains(clickTarget);
    const isClickInsideInput =
      this.inputElementRef()?.nativeElement.contains(clickTarget);

    if (!isClickInsideOverlay && !isClickInsideInput) {
      // Click is outside, close the overlay
      this.panelOpen = false;
      this.isInputFocused = false;
    }
  }

  openPanel(): void {
    this.panelOpen = true;
    this.overlayDir().overlayRef?.updateSize({
      width: this.inputElementRef().nativeElement.offsetWidth,
    });
  }

  positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: 6,
    },
  ];
  selectOption(option: { label: string; value: string }): void {
    if (this.isMultiSelect()) {
      // Handle multi-select scenario
    } else {
      this.currentInputValue = option.label;
      if (this.selectedOptions.length) {
        this.selectedOptions[0] = option;
      } else {
        this.selectedOptions.push(option);
      }
      this.panelOpen = false;
    }
    this.updateFormControl();
    this.refreshOptions();
  }

  updateFormControl(): void {
    if (this.isMultiSelect()) {
      const formArray = this.groupName().get(this.controlName()) as FormArray;
      // Clear the array
      while (formArray.length !== 0) {
        formArray.removeAt(0);
      }
      // Add the new values
      this.selectedOptions.forEach((option) =>
        formArray.push(new FormControl(option.value))
      );
    } else {
      this.groupName()
        .get(this.controlName())
        ?.setValue(this.selectedOptions[0]?.value);
    }
  }
  removeSelectedOption(index: number): void {
    this.selectedOptions.splice(index, 1);
    this.updateFormControl();
    this.refreshOptions();
  }
  refreshOptions(): void {
    // Assuming 'filteredOptions' and 'selectedOptions' are both arrays of options
    // and each option has a unique identifier (e.g., 'value').
    // Adjust the comparison condition based on your data structure.
    this.filteredOptions = this.originalOptions.filter(
      (option) =>
        !this.selectedOptions.some(
          (selected) => selected.value === option.value
        )
    );
  }

  toggleOverlay(): void {
    this.panelOpen = !this.panelOpen;
  }
  openInput() {
    this.isInputFocused = true;
    // Set the input's value to an empty string
    this.searchControl.setValue('');
    this.openPanel(); // Assuming this is a method you want to call
    this.inputElement().nativeElement.focus();
  }
}
